<template>
  <div class="custom-dropdown">
    <label class="block text-sm font-medium text-gray-300 mb-1"
      >Kategorija</label
    >

    <!-- Dropdown toggle button -->
    <div
      class="dropdown-toggle w-full bg-gray-700 border border-gray-600 rounded-md py-2 px-3 text-white focus:outline-none focus:ring-2 focus:ring-green-500 flex justify-between items-center cursor-pointer"
      @click="isOpen = !isOpen"
    >
      <span>{{ selectedCategoryName || 'Sve kategorije' }}</span>
      <svg
        class="w-4 h-4 text-gray-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M19 9l-7 7-7-7"
        ></path>
      </svg>
    </div>

    <!-- Pojednostavljeni dropdown menu - sve na jednom nivou -->
    <div
      v-if="isOpen"
      class="dropdown-menu w-full mt-1 bg-gray-700 border border-gray-600 rounded-md max-h-60 overflow-y-auto absolute z-10"
    >
      <!-- "Sve kategorije" opcija -->
      <div
        class="py-2 px-3 hover:bg-gray-600 cursor-pointer border-b border-gray-600 text-white"
        @click="selectCategory('', '')"
      >
        Sve kategorije
      </div>

      <!-- Glavne kategorije prikazane bold -->
      <div v-for="category in categories" :key="category.id">
        <div
          class="py-2 px-3 hover:bg-gray-600 cursor-pointer text-white font-medium"
          @click="selectCategory(category.id, category.name)"
        >
          {{ category.name }}
        </div>

        <!-- Podkategorije odmah ispod, sa uvlačenjem -->
        <div v-if="category.children && category.children.length > 0">
          <div
            v-for="child in category.children"
            :key="child.id"
            class="py-1.5 pl-8 pr-3 hover:bg-gray-600 cursor-pointer text-white text-sm"
            @click="selectCategory(child.id, child.name)"
          >
            {{ child.name }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import categoryService from '@/services/categoryService';

interface Category {
  id: string;
  name: string;
  level?: number;
  parentId?: string;
  children?: Category[];
}

export default defineComponent({
  name: 'CategoryDropdown',
  props: {
    modelValue: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const categories = ref<Category[]>([]);
    const isOpen = ref(false);

    // Dohvati ime selektovane kategorije
    const selectedCategoryName = computed(() => {
      if (!props.modelValue) return '';

      // Prvo proveri da li je to glavna kategorija
      const mainCategory = categories.value.find(
        (c) => c.id === props.modelValue
      );
      if (mainCategory) return mainCategory.name;

      // Ako nije glavna, onda proveri podkategorije
      for (const category of categories.value) {
        if (category.children) {
          const subCategory = category.children.find(
            (c) => c.id === props.modelValue
          );
          if (subCategory) return subCategory.name;
        }
      }

      return '';
    });

    const fetchCategories = async () => {
      try {
        categories.value = await categoryService.getCategories();
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        categories.value = [];
      }
    };

    const selectCategory = (categoryId: string, categoryName: string) => {
      emit('update:modelValue', categoryId);
      isOpen.value = false;
    };

    // Zatvori dropdown ako se klikne izvan komponente
    const handleClickOutside = (event: MouseEvent) => {
      const dropdown = document.querySelector('.custom-dropdown');
      if (
        dropdown &&
        !dropdown.contains(event.target as Node) &&
        isOpen.value
      ) {
        isOpen.value = false;
      }
    };

    onMounted(() => {
      fetchCategories();
      document.addEventListener('click', handleClickOutside);
    });

    onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside);
    });

    return {
      categories,
      isOpen,
      selectedCategoryName,
      selectCategory,
    };
  },
});
</script>

<style scoped>
.dropdown-menu {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

/* Custom scrollbar styling for dropdown */
.dropdown-menu::-webkit-scrollbar {
  width: 8px;
}

.dropdown-menu::-webkit-scrollbar-track {
  background: #4a5568;
}

.dropdown-menu::-webkit-scrollbar-thumb {
  background-color: #2d3748;
  border-radius: 4px;
}

.dropdown-menu::-webkit-scrollbar-thumb:hover {
  background-color: #1a202c;
}

.custom-dropdown {
  position: relative;
}
</style>
